import { useMemo, useState } from 'react';
import reportWerksLogo from './assets/reportwerks-logo.png';
import { trackGa4Event, trackLeadFormSubmission } from './analytics';
import { auditLeadEndpoint, grantAuditAccess, hasAuditAccess } from './auditLeadAccess';
import AuditDownloadButton from './AuditDownloadButton';
import { submitMauticLead } from './leadSubmission';
import './MarketingDataAuditTool.css';

const auditEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const auditSegmentId = 31;
const auditResponderId = 80;
const auditNotificationId = 81;
const auditEmailContactId = 190789;

export const auditSections = [
  {
    id: 'A',
    name: 'Connection and coverage',
    prompt: 'Do your reports include the full marketing picture?',
    intro: 'Missing sources and unstable connections create blind spots before analysis even begins.',
    color: '#ff7417',
    why: 'A polished dashboard cannot compensate for missing accounts, stale permissions, or mismatched reporting windows.',
    redFlag: 'The monthly report begins with someone asking, "Which spreadsheet is the latest one?"',
    recommendation: 'Inventory every reporting source and assign a clear owner.',
    questions: [
      'Every active advertising, analytics, email, CRM, ecommerce, or fundraising platform used in reporting is documented.',
      'The team knows who owns each account, connection, permission set, and billing relationship.',
      'Expired tokens, revoked permissions, and broken data connections are detected before a report is due.',
      'Date ranges, time zones, currencies, and account-level filters are aligned across platforms.',
      'Manual exports are limited, documented, and repeatable by more than one person.',
    ],
  },
  {
    id: 'B',
    name: 'Conversion integrity',
    prompt: 'Are you counting outcomes or convenient signals?',
    intro: 'When page views, video engagement, calls, and purchases share the same conversion label, reporting can drift away from reality.',
    color: '#f20d7a',
    why: 'Automated bidding and budget decisions are only as good as the outcome signals sent back to each platform.',
    redFlag: 'A campaign reports more conversions than anyone can explain, but revenue does not move with them.',
    recommendation: 'Validate primary conversions, deduplication, and value passing.',
    questions: [
      'Primary conversions represent completed business outcomes such as donations, purchases, qualified leads, or approved applications.',
      'Microconversions such as page views, video engagement, and newsletter signups are reported separately from primary outcomes.',
      'Unique transaction or event IDs prevent duplicate conversions from reloads, redirects, and repeated tag firing.',
      'Revenue, gift value, or lead value is passed accurately and is not assigned to general engagement events.',
      'Cross-domain, embedded-form, and payment-processor tracking has been tested from click through completion.',
    ],
  },
  {
    id: 'C',
    name: 'Metric consistency',
    prompt: 'Does everyone mean the same thing by performance?',
    intro: 'Shared definitions make cross-channel analysis possible. Without them, every chart can be technically correct and still tell a conflicting story.',
    color: '#6126d9',
    why: 'Consistent definitions let leadership compare channels without forcing analysts to explain a different rule for every number.',
    redFlag: 'Two reports use the label "revenue," but one shows platform-attributed value and the other shows settled transactions.',
    recommendation: 'Document shared KPI, channel, and campaign definitions.',
    questions: [
      'Campaign, channel, audience, and initiative naming standards are documented and consistently used.',
      'The organization has shared definitions for conversions, revenue, ROAS, cost per acquisition, and other executive KPIs.',
      'Paid, grant-funded, organic, awareness, and direct-response activity can be viewed separately when their objectives differ.',
      'Source, medium, campaign, and platform mappings are documented so the same traffic is not classified differently across reports.',
    ],
  },
  {
    id: 'D',
    name: 'Revenue and attribution',
    prompt: 'Can you connect marketing activity to verified value?',
    intro: 'Platforms estimate influence. Your CRM, ecommerce, or fundraising system records the actual transaction. Strong reporting respects the difference.',
    color: '#0797e5',
    why: 'Reconciliation separates directional channel insight from verified organizational revenue and prevents double-counting.',
    redFlag: 'Google, Meta, and LinkedIn each claim the same conversion, and the combined total exceeds actual revenue.',
    recommendation: 'Reconcile platform revenue to the system of record.',
    questions: [
      'Platform-reported revenue is regularly reconciled to the CRM, ecommerce, accounting, or fundraising system of record.',
      'Attribution windows and models are documented so stakeholders understand why platforms may claim different credit.',
      'New versus returning customers or donors, one-time versus recurring gifts, and other important value segments are available where applicable.',
      'Refunds, cancellations, chargebacks, offline conversions, and other post-transaction adjustments are considered in reporting.',
      'The team does not add attributed revenue from multiple ad platforms together as if each platform measured exclusive sales.',
    ],
  },
  {
    id: 'E',
    name: 'Reporting workflow',
    prompt: 'Can your team produce the report without rebuilding it?',
    intro: 'A reporting process should survive vacations, staff changes, new clients, and additional platforms without becoming a monthly emergency.',
    color: '#2d73d5',
    why: 'A repeatable workflow creates more time for analysis, recommendations, and client service instead of data assembly.',
    redFlag: 'The report is late because one platform changed its export format or one team member is unavailable.',
    recommendation: 'Build one repeatable reporting template and review process.',
    questions: [
      'Recurring reports can be produced from a documented template without recreating charts, formulas, and filters each cycle.',
      'Commentary explains why performance changed and what should happen next, not merely whether a metric rose or fell.',
      'Missing data, anomalies, tracking breaks, and unusual spikes are flagged before the report reaches a client or executive.',
      'Report ownership, review steps, delivery schedule, and stakeholder distribution are clearly assigned.',
    ],
  },
  {
    id: 'F',
    name: 'Executive usefulness',
    prompt: 'Does the report help someone make a decision?',
    intro: 'The goal is not to fit every metric on one page. It is to make the next decision clearer for the person reading it.',
    color: '#f8b700',
    why: 'Reports create value when they convert data into shared understanding, priorities, and accountable next steps.',
    redFlag: 'The meeting ends with everyone agreeing the dashboard is impressive, but no one knows what decision was made.',
    recommendation: 'Attach an action, owner, priority, and timeframe to every finding.',
    questions: [
      'Each report answers a defined business question such as where to invest, what to fix, or which audience is creating value.',
      'KPIs are tied to the objective of the campaign or program rather than chosen only because the platform makes them easy to export.',
      'Findings include a recommended action, owner, priority, and timeframe when a meaningful issue or opportunity is identified.',
      'Executives receive a concise summary while channel specialists can access the supporting detail needed to investigate and act.',
    ],
  },
];

export const getScoreBand = (score) => {
  if (score >= 44) return {
    label: 'Trusted system',
    color: '#0797e5',
    summary: 'Your foundation is strong enough to focus on speed, automation, predictive insight, and deeper segmentation.',
  };
  if (score >= 31) return {
    label: 'Usable but vulnerable',
    color: '#f8b700',
    summary: 'The report works, but inconsistent definitions or person-dependent steps can create avoidable risk.',
  };
  if (score >= 16) return {
    label: 'Decision risk',
    color: '#f20d7a',
    summary: 'Leadership may be making budget or strategy choices from incomplete, duplicated, or poorly reconciled data.',
  };
  return {
    label: 'Reporting emergency',
    color: '#e83c32',
    summary: 'Stabilize the measurement foundation and establish a reliable reporting baseline before adding more dashboards.',
  };
};

export const calculateReportingTax = ({ platforms, minutes, reports, hourlyRate }) => {
  const clean = (value) => Math.max(0, Number(value) || 0);
  const monthlyHours = (clean(platforms) * clean(minutes) * clean(reports)) / 60;
  const annualHours = monthlyHours * 12;
  return {
    monthlyHours,
    annualHours,
    annualCost: annualHours * clean(hourlyRate),
  };
};

const scrollToolToTop = () => {
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function' && process.env.NODE_ENV !== 'test') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const answerOptions = [
  { value: 2, label: 'Yes', detail: 'Documented and repeatable' },
  { value: 1, label: 'Partly', detail: 'Works with manual effort' },
  { value: 0, label: 'No', detail: 'Missing or unreliable' },
];

function ToolHeader({ unlocked }) {
  return (
    <header className="tool-header">
      <div className="shell tool-nav">
        <a className="tool-logo" href="/" aria-label="ReportWerks home">
          <img src={reportWerksLogo} alt="ReportWerks" />
        </a>
        {unlocked ? <AuditDownloadButton /> : <a href="/privacy-policy">Privacy &amp; data use</a>}
      </div>
    </header>
  );
}

function AuditAccessGate({ onUnlock }) {
  const [formData, setFormData] = useState({ firstName: '', email: '', consent: false });
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState({ type: 'idle', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    const submittedForm = new FormData(event.currentTarget);
    if (String(submittedForm.get('company') || '').trim()) return;
    const nextErrors = {};
    if (!formData.firstName.trim()) nextErrors.firstName = 'Enter your first name.';
    if (!formData.email.trim()) nextErrors.email = 'Enter your work email.';
    else if (!auditEmailPattern.test(formData.email.trim())) nextErrors.email = 'Enter a valid email address.';
    if (!formData.consent) nextErrors.consent = 'Confirm that we can email the audit and reporting guidance to you.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitState({ type: 'error', message: 'Please fix the highlighted fields.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitState({ type: 'idle', message: '' });
    const payload = {
      name: formData.firstName.trim(),
      firstname: formData.firstName.trim(),
      email: formData.email.trim(),
      consent: 'Yes',
      domain: typeof window !== 'undefined' ? window.location.hostname : '',
      goal: 'Marketing Data Confidence Audit online tool access',
      message: 'Unlocked and started the ReportWerks Marketing Data Confidence Audit online tool.',
      source: 'ReportWerks Marketing Data Confidence Audit online tool gate',
      submitted_at: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      emailid: auditNotificationId,
      emailContactId: auditEmailContactId,
      responder: auditResponderId,
      segment: auditSegmentId,
    };

    try {
      const result = await submitMauticLead(auditLeadEndpoint, payload, 'Unable to unlock the audit right now.');
      grantAuditAccess(result.auditAccess);
      trackLeadFormSubmission({
        formContext: {
          form_id: 'reportwerks-marketing-data-confidence-audit-tool-gate',
          form_name: 'Marketing Data Confidence Audit online tool access',
          form_location: '/marketing-data-confidence-audit-tool',
          lead_type: 'lead_magnet_tool_access',
          page_location: typeof window !== 'undefined' ? window.location.href : 'https://www.reportwerks.com/marketing-data-confidence-audit-tool',
          page_title: typeof document !== 'undefined' ? document.title : 'Marketing Data Confidence Audit Tool',
          source_component: 'interactive_audit_gate',
        },
        includeSignUp: true,
        includeCloseConvertLead: false,
      });
      onUnlock();
    } catch (error) {
      setSubmitState({ type: 'error', message: error.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main id="main-content" className="tool-gate">
      <section className="tool-gate-hero">
        <div className="shell tool-gate-grid">
          <div className="tool-gate-copy">
            <span className="tool-eyebrow">Free interactive assessment</span>
            <h1>Find the reporting gaps putting your decisions at risk.</h1>
            <p>Answer 27 practical checks and get an instant confidence score, your three highest-priority fixes, and a focused 30-day action sequence.</p>
            <ul>
              <li><strong>6</strong> reporting risk areas</li>
              <li><strong>10</strong> minutes to complete</li>
              <li><strong>3</strong> prioritized next steps</li>
            </ul>
          </div>
          <div className="tool-gate-card">
            <span className="tool-eyebrow">Unlock your free audit</span>
            <h2>Where should we send your companion scorecard?</h2>
            <p>Enter your name and work email to begin. We’ll also email the PDF companion from ReportWerks.</p>
            {submitState.type === 'error' ? <div className="tool-gate-error" role="alert">{submitState.message}</div> : null}
            <form noValidate onSubmit={submit}>
              <label htmlFor="tool-first-name">First name<input id="tool-first-name" name="firstName" type="text" autoComplete="given-name" value={formData.firstName} onChange={update} aria-invalid={Boolean(errors.firstName)} />{errors.firstName ? <span>{errors.firstName}</span> : null}</label>
              <label htmlFor="tool-email">Work email<input id="tool-email" name="email" type="email" autoComplete="email" value={formData.email} onChange={update} aria-invalid={Boolean(errors.email)} />{errors.email ? <span>{errors.email}</span> : null}</label>
              <input aria-hidden="true" autoComplete="new-password" name="company" className="honeypot" tabIndex="-1" type="text" />
              <label className="tool-gate-consent"><input name="consent" type="checkbox" checked={formData.consent} onChange={update} /><span>Email me the audit and practical reporting guidance. I can unsubscribe anytime.</span></label>
              {errors.consent ? <span className="tool-gate-field-error">{errors.consent}</span> : null}
              <button className="tool-primary-action" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Unlocking your audit…' : 'Unlock my free audit'} <span aria-hidden="true">&rarr;</span></button>
              <small>Your information is used to deliver this resource. See our <a href="/privacy-policy">Privacy Policy</a>.</small>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}

function Intro({ onStart }) {
  return (
    <main id="main-content" className="tool-intro">
      <section className="tool-hero">
        <div className="tool-hero-grid shell">
          <div className="tool-hero-copy">
            <span className="tool-eyebrow">Free interactive assessment</span>
            <h1>Can you trust the reporting system behind your decisions?</h1>
            <p>Score 27 checks across the full path from platform data to executive action. Get an instant confidence score and the three areas to fix first.</p>
            <button className="tool-primary-action" type="button" onClick={onStart}>Start the assessment <span aria-hidden="true">&rarr;</span></button>
            <small>Access unlocked. Your progress stays available while you complete the assessment.</small>
          </div>
          <div className="tool-preflight" aria-label="Assessment overview">
            <div className="tool-preflight-score"><span>Potential score</span><strong>54</strong></div>
            <div className="tool-preflight-spine" aria-hidden="true">
              {auditSections.map((section) => <span key={section.id} style={{ background: section.color }} />)}
            </div>
            <div className="tool-preflight-copy">
              <strong>6 reporting risk areas</strong>
              <p>Connections. Conversions. Metrics. Revenue. Workflow. Decisions.</p>
            </div>
            <div className="tool-preflight-note">Built from the ReportWerks Marketing Data Confidence Audit.</div>
          </div>
        </div>
      </section>
      <section className="tool-method shell" aria-labelledby="tool-method-title">
        <div>
          <span className="tool-eyebrow">Score the system, not the workaround</span>
          <h2 id="tool-method-title">Be strict. Repeatable beats heroic.</h2>
        </div>
        <div className="tool-method-grid">
          {answerOptions.map((option) => (
            <article key={option.value}>
              <span>{option.value}</span>
              <h3>{option.label}</h3>
              <p>{option.value === 2 ? 'The process is documented, repeatable, and consistently followed.' : option.value === 1 ? 'It works in some cases, depends on one person, or requires manual cleanup.' : 'The process is missing, unreliable, or not understood by the team.'}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function ProgressSpine({ activeIndex, answers, sectionScores }) {
  const answeredCount = Object.keys(answers).length;
  return (
    <aside className="tool-progress" aria-label="Assessment progress">
      <div className="tool-progress-total">
        <span>Live score</span>
        <strong>{answeredCount ? Object.values(answers).reduce((sum, value) => sum + value, 0) : '-'}</strong>
            <small>out of 54</small>
      </div>
      <ol>
        {auditSections.map((section, index) => {
          const answered = section.questions.filter((_, questionIndex) => answers[`${section.id}${questionIndex + 1}`] !== undefined).length;
          return (
            <li key={section.id} className={index === activeIndex ? 'active' : ''}>
              <span className="tool-progress-color" style={{ background: section.color }} />
              <div><strong>{section.id}. {section.name}</strong><small>{answered}/{section.questions.length} checks</small></div>
              <b>{sectionScores[section.id] ?? '-'}</b>
            </li>
          );
        })}
      </ol>
      <div className="tool-progress-bar"><span style={{ width: `${(answeredCount / 27) * 100}%` }} /></div>
      <small>{answeredCount} of 27 checks complete</small>
    </aside>
  );
}

function Assessment({ answers, currentSection, onAnswer, onBack, onContinue, sectionScores }) {
  const section = auditSections[currentSection];
  const complete = section.questions.every((_, index) => answers[`${section.id}${index + 1}`] !== undefined);
  return (
    <main id="main-content" className="tool-assessment shell">
      <ProgressSpine activeIndex={currentSection} answers={answers} sectionScores={sectionScores} />
      <section className="tool-question-panel" aria-labelledby={`section-${section.id}-title`}>
        <div className="tool-section-heading">
          <span className="tool-section-letter" style={{ color: section.color }}>{section.id}</span>
          <div>
            <span className="tool-eyebrow">{section.name}</span>
            <h1 id={`section-${section.id}-title`}>{section.prompt}</h1>
            <p>{section.intro}</p>
          </div>
        </div>
        <div className="tool-question-list">
          {section.questions.map((question, index) => {
            const questionId = `${section.id}${index + 1}`;
            return (
              <fieldset key={questionId} className="tool-question">
                <legend><span>{questionId}</span>{question}</legend>
                <div className="tool-answer-grid">
                  {answerOptions.map((option) => (
                    <label key={option.value} className={answers[questionId] === option.value ? 'selected' : ''}>
                      <input type="radio" name={questionId} value={option.value} checked={answers[questionId] === option.value} onChange={() => onAnswer(questionId, option.value)} />
                      <strong>{option.label}</strong><small>{option.value}</small>
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
        <div className="tool-context-note"><strong>Why this matters</strong><p>{section.why}</p></div>
        <div className="tool-red-flag"><strong>Red flag:</strong> {section.redFlag}</div>
        <div className="tool-section-actions">
          <button type="button" className="tool-text-action" onClick={onBack}>{currentSection === 0 ? 'Back to overview' : 'Previous section'}</button>
          <button type="button" className="tool-primary-action" disabled={!complete} onClick={onContinue}>{currentSection === auditSections.length - 1 ? 'See my results' : 'Continue'} <span aria-hidden="true">&rarr;</span></button>
        </div>
        {!complete ? <p className="tool-completion-hint" role="status">Answer every check in this section to continue.</p> : null}
      </section>
    </main>
  );
}

function ReportingTaxCalculator() {
  const [inputs, setInputs] = useState({ platforms: '', minutes: '', reports: '', hourlyRate: '' });
  const tax = calculateReportingTax(inputs);
  const update = (event) => setInputs((current) => ({ ...current, [event.target.name]: event.target.value }));
  const hasEstimate = Object.values(inputs).some((value) => value !== '');
  return (
    <section className="tool-tax" aria-labelledby="reporting-tax-title">
      <div className="tool-tax-heading">
        <span className="tool-eyebrow">Make the hidden work visible</span>
        <h2 id="reporting-tax-title">Estimate your reporting tax.</h2>
        <p>Include logging in, exporting, cleaning, copying, reconciling, and rebuilding charts.</p>
      </div>
      <div className="tool-tax-grid">
        <div className="tool-tax-inputs">
          <label>Platforms<input type="number" min="0" inputMode="numeric" name="platforms" value={inputs.platforms} onChange={update} /></label>
          <span>&times;</span>
          <label>Minutes per platform<input type="number" min="0" inputMode="numeric" name="minutes" value={inputs.minutes} onChange={update} /></label>
          <span>&times;</span>
          <label>Reports per month<input type="number" min="0" inputMode="numeric" name="reports" value={inputs.reports} onChange={update} /></label>
          <label className="tool-tax-rate">Loaded hourly cost<input type="number" min="0" inputMode="decimal" name="hourlyRate" value={inputs.hourlyRate} onChange={update} placeholder="$" /></label>
        </div>
        <div className="tool-tax-result" aria-live="polite">
          <span>Annual reporting tax</span>
          <strong>{hasEstimate ? Math.round(tax.annualHours).toLocaleString() : '-'}</strong>
          <b>hours</b>
          <p>{hasEstimate ? tax.annualCost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '$0'} estimated annual labor cost</p>
        </div>
      </div>
    </section>
  );
}

function Results({ answers, sectionScores, onRestart }) {
  const total = Object.values(answers).reduce((sum, value) => sum + value, 0);
  const band = getScoreBand(total);
  const rankedSections = [...auditSections].sort((a, b) => {
    const aRatio = sectionScores[a.id] / (a.questions.length * 2);
    const bRatio = sectionScores[b.id] / (b.questions.length * 2);
    return aRatio - bRatio;
  });
  const priorities = rankedSections.slice(0, 3);
  return (
    <main id="main-content" className="tool-results">
      <section className="tool-results-hero">
        <div className="shell tool-results-grid">
          <div className="tool-score-orbit" style={{ '--score-color': band.color, '--score-angle': `${(total / 54) * 360}deg` }}>
            <div><strong>{total}</strong><span>out of 54</span></div>
          </div>
          <div className="tool-results-copy">
            <span className="tool-eyebrow">Your Marketing Data Confidence Score</span>
            <h1>{band.label}</h1>
            <p>{band.summary}</p>
            <div className="tool-result-actions">
              <button className="tool-primary-action" type="button" onClick={() => window.print()}>Print my results</button>
              <button className="tool-text-action tool-text-action--light" type="button" onClick={onRestart}>Retake the audit</button>
            </div>
          </div>
        </div>
      </section>
      <section className="tool-scorecard shell" aria-labelledby="scorecard-title">
        <div className="tool-scorecard-heading">
          <span className="tool-eyebrow">Bring it together</span>
          <h2 id="scorecard-title">Your six reporting risk areas.</h2>
        </div>
        <div className="tool-score-grid">
          {auditSections.map((section) => (
            <article key={section.id}>
              <span style={{ background: section.color }}>{section.id}</span>
              <div><h3>{section.name}</h3><p>{section.prompt}</p></div>
              <strong>{sectionScores[section.id]}<small>/{section.questions.length * 2}</small></strong>
            </article>
          ))}
        </div>
      </section>
      <section className="tool-priorities shell" aria-labelledby="priorities-title">
        <div className="tool-priority-heading">
          <span className="tool-eyebrow">Choose foundations before polish</span>
          <h2 id="priorities-title">Your three highest-priority fixes.</h2>
          <p>Start with the weakest systems. Give each fix an owner and a due date within the next 30 days.</p>
        </div>
        <ol>
          {priorities.map((section, index) => (
            <li key={section.id}>
              <span>{index + 1}</span>
              <div><small>{section.name}</small><strong>{section.recommendation}</strong></div>
              <b>{sectionScores[section.id]}/{section.questions.length * 2}</b>
            </li>
          ))}
        </ol>
        <div className="tool-sequence">
          <article><span>Days 1-7</span><strong>Stabilize</strong><p>Inventory platforms, verify access, confirm date settings, and identify the system of record.</p></article>
          <article><span>Days 8-21</span><strong>Validate</strong><p>Test primary conversions, deduplication, revenue values, and cross-domain measurement.</p></article>
          <article><span>Days 22-30</span><strong>Standardize</strong><p>Document KPI definitions, build the recurring template, and assign actions to named owners.</p></article>
        </div>
      </section>
      <ReportingTaxCalculator />
      <section className="tool-results-cta">
        <div className="shell">
          <div><span className="tool-eyebrow">Keep the scorecard</span><h2>Turn the result into a working plan.</h2><p>Get the printable audit for notes, evidence, owners, and due dates, or see how ReportWerks supports a repeatable reporting workflow.</p></div>
          <div><AuditDownloadButton className="tool-primary-action">Download the companion PDF</AuditDownloadButton><a className="tool-secondary-action" href="/book">Explore ReportWerks</a></div>
        </div>
      </section>
    </main>
  );
}

export default function MarketingDataAuditTool() {
  const [view, setView] = useState(() => hasAuditAccess() ? 'intro' : 'gate');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const sectionScores = useMemo(() => Object.fromEntries(auditSections.map((section) => [
    section.id,
    section.questions.reduce((sum, _, index) => sum + (answers[`${section.id}${index + 1}`] ?? 0), 0),
  ])), [answers]);

  const moveTo = (nextView) => {
    setView(nextView);
    scrollToolToTop();
  };
  const start = () => {
    setCurrentSection(0);
    moveTo('assessment');
  };
  const unlockAndStart = () => {
    setCurrentSection(0);
    moveTo('assessment');
  };
  const back = () => {
    if (currentSection === 0) moveTo('intro');
    else {
      setCurrentSection((index) => index - 1);
      scrollToolToTop();
    }
  };
  const next = () => {
    if (currentSection < auditSections.length - 1) {
      setCurrentSection((index) => index + 1);
      scrollToolToTop();
      return;
    }
    const total = Object.values(answers).reduce((sum, value) => sum + value, 0);
    trackGa4Event('marketing_audit_complete', {
      form_id: 'reportwerks-marketing-data-confidence-audit-tool',
      form_location: '/marketing-data-confidence-audit-tool',
      source_component: 'interactive_audit',
      value: total,
    });
    moveTo('results');
  };
  const restart = () => {
    setAnswers({});
    setCurrentSection(0);
    moveTo('intro');
  };

  return (
    <div className="audit-tool-page">
      <ToolHeader unlocked={view !== 'gate'} />
      {view === 'gate' ? <AuditAccessGate onUnlock={unlockAndStart} /> : null}
      {view === 'intro' ? <Intro onStart={start} /> : null}
      {view === 'assessment' ? <Assessment answers={answers} currentSection={currentSection} onAnswer={(id, value) => setAnswers((current) => ({ ...current, [id]: value }))} onBack={back} onContinue={next} sectionScores={sectionScores} /> : null}
      {view === 'results' ? <Results answers={answers} sectionScores={sectionScores} onRestart={restart} /> : null}
      <footer className="tool-footer"><div className="shell"><span>ReportWerks by DigitalWerks</span><a href="/privacy-policy">Privacy Policy</a></div></footer>
    </div>
  );
}
